import { useTranslation } from 'react-i18next'
import {
  PublicAssetFrontendApi$AssetComponentDtoKey,
  type PublicAssetFrontendApi$AssetComponentDto,
} from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import useSerialLookup from './useSerialLookup'
import useVoiceCallMode from './useVoiceCallMode'
import HomeOverlays from './HomeOverlays'
import {
  AssetShell,
  CardsSkeleton,
  PreviewHeader,
  PreviewNav,
  RecentStrip,
  ResourceRow,
  RowGroup,
  SerialSearch,
  SupportCard,
} from './tileKit'
import { layoutComponents } from './componentLayout'
import { usePreviewFonts } from './fonts'
import type { HomeModel } from './useHomeModel'

const KEY = PublicAssetFrontendApi$AssetComponentDtoKey

/**
 * The asset frontend home, in the same design as the asset view: one column
 * on the 16px gutter; a card holding the serial search where the asset view
 * has its identity card; the three highest-priority components as support
 * boxes; every other component in one compact list. Recently visited sits
 * right under the search, so it is on screen on a phone.
 *
 * Behaviour is unchanged: the voice call opens straight away (or dials, in
 * instant mode) and every other component first asks which asset is meant.
 */
export default function AssetFrontendHomeDesign({ model }: { model: HomeModel }) {
  usePreviewFonts(['schibsted'])
  const lookup = useSerialLookup()
  const { t } = useTranslation()
  const { all, support, rest } = layoutComponents(model.components)
  const voiceComponent = all.find((c) => c.key === KEY.VoiceCall) ?? null
  const voiceCall = useVoiceCallMode(voiceComponent)
  const loading = model.isLoading && model.components == null

  const phoneFor = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    voiceComponent != null && c.id === voiceComponent.id && voiceCall.mode === 'instant_call'
      ? voiceCall.phone
      : null

  const isCall = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.key === KEY.VideoCall || c.key === KEY.VoiceCall

  const callSubtitle = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.key === KEY.VideoCall
      ? t('component.videocall.subtitlePrompt')
      : t('component.voicecall.subtitlePrompt')

  // The organisation's own description wins; the call prompts are the fallback.
  const subtitleFor = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.description ?? (isCall(c) ? callSubtitle(c) : undefined)

  // Voice opens its panel; everything else asks for the asset first.
  const open = (c: PublicAssetFrontendApi$AssetComponentDto) => model.selectBottom(c)

  return (
    <AssetShell
      accent={model.accent}
      nav={
        // Hidden while a drawer is open, as on the asset view; it would sit on
        // top of the drawer's content otherwise.
        model.selectAssetOpen ||
        model.accountOpen ||
        model.scanOpen ||
        model.voiceComponent != null ||
        model.firstTimeOpen ? undefined : (
          <PreviewNav
            active="home"
            onAccount={model.openAccount}
            onHome={model.goHome}
            onScan={model.openScan}
          />
        )
      }
    >
      <PreviewHeader
        infoLabel={t('home.whatIsThis.title')}
        logoUrl={model.logoUrl}
        onInfo={model.openFirstTime}
      />

      <div className="flex flex-col gap-3 px-4">
        {/* Finding the asset: the home's counterpart to the asset view's
            identity card. */}
        <div className="rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] px-4 pb-3.5 pt-3.5 sm:pb-4 sm:pt-4 text-[var(--rda-on-surface)]">
          <h1 className="text-[11px] font-semibold uppercase leading-none tracking-[0.16em] sm:text-[12px] text-[var(--rda-muted)]">
            {t('assetSelection.title')}
          </h1>
          <div className="mt-2.5 sm:mt-3">
            <SerialSearch
              ariaLabel={t('assetView.serialnumber.shortened')}
              lookup={lookup}
              placeholder={model.searchPlaceholder}
              onScan={model.openScan}
            />
          </div>
        </div>

        {/* Recently visited is the quickest way back to an asset, so it sits
            with the search rather than below the fold. */}
        {model.recent.length > 0 ? <RecentStrip recent={model.recent} /> : null}

        {loading ? <CardsSkeleton /> : null}

        {!loading && support.length > 0 ? (
          <div
            className={
              'grid gap-2 ' +
              (support.length === 3
                ? 'grid-cols-3'
                : support.length === 2
                  ? 'grid-cols-2'
                  : 'grid-cols-1')
            }
          >
            {support.map((c) => (
              <SupportCard
                key={`s-${c.id}`}
                component={c}
                instantPhone={phoneFor(c)}
                subtitle={subtitleFor(c)}
                onClick={() => open(c)}
              />
            ))}
          </div>
        ) : null}

        {/* Everything after the boxes as one compact list - names only, so
            "Recently visited" still fits on a phone screen. */}
        {!loading && rest.length > 0 ? (
          <RowGroup>
            {rest.map((c) => (
              <ResourceRow
                key={`r-${c.id}`}
                grouped
                tinted
                component={c}
                instantPhone={phoneFor(c)}
                onClick={() => open(c)}
              />
            ))}
          </RowGroup>
        ) : null}

      </div>

      <HomeOverlays model={model} />
    </AssetShell>
  )
}
